import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Play,
  List,
  BarChart3,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const QueueManagement = () => {
  const [queueStats, setQueueStats] = useState(null);
  const [stuckAnalyses, setStuckAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [fixResults, setFixResults] = useState(null);
  const [error, setError] = useState(null);

  const { getIdToken } = useAuth();

  const fetchQueueStats = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/queue/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch queue stats');
      const data = await response.json();
      setQueueStats(data);
    } catch (err) {
      console.error('Error fetching queue stats:', err);
    }
  };

  const fetchStuckAnalyses = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/stuck-analyses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch stuck analyses');
      const data = await response.json();
      setStuckAnalyses(data);
    } catch (err) {
      console.error('Error fetching stuck analyses:', err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchQueueStats(), fetchStuckAnalyses()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleFixStuckAnalyses = async () => {
    if (!window.confirm(`Are you sure you want to re-queue ${stuckAnalyses.length} stuck analyses?`)) {
      return;
    }

    setFixing(true);
    setFixResults(null);

    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/stuck-analyses/fix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fix stuck analyses');
      const results = await response.json();
      setFixResults(results);

      // Refresh data after fixing
      setTimeout(fetchAllData, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setFixing(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return 'Unknown';
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Loading queue data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-blue-600" />
          Queue Management
        </h2>
        <button
          onClick={fetchAllData}
          className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Queue Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <div className="flex items-center text-blue-600 mb-1">
            <Clock className="w-4 h-4 mr-1" />
            <span className="text-sm">Waiting</span>
          </div>
          <div className="text-2xl font-bold text-blue-700">
            {queueStats?.waiting || 0}
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <div className="flex items-center text-green-600 mb-1">
            <Play className="w-4 h-4 mr-1" />
            <span className="text-sm">Active</span>
          </div>
          <div className="text-2xl font-bold text-green-700">
            {queueStats?.active || 0}
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
          <div className="flex items-center text-purple-600 mb-1">
            <CheckCircle className="w-4 h-4 mr-1" />
            <span className="text-sm">Completed</span>
          </div>
          <div className="text-2xl font-bold text-purple-700">
            {queueStats?.completed || 0}
          </div>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-100">
          <div className="flex items-center text-red-600 mb-1">
            <XCircle className="w-4 h-4 mr-1" />
            <span className="text-sm">Failed</span>
          </div>
          <div className="text-2xl font-bold text-red-700">
            {queueStats?.failed || 0}
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
          <div className="flex items-center text-orange-600 mb-1">
            <BarChart3 className="w-4 h-4 mr-1" />
            <span className="text-sm">Concurrency</span>
          </div>
          <div className="text-2xl font-bold text-orange-700">
            {queueStats?.concurrency || 0}
          </div>
        </div>
      </div>

      {/* Stuck Analyses Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-orange-500" />
            Stuck Analyses
            {stuckAnalyses.length > 0 && (
              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-sm rounded-full">
                {stuckAnalyses.length}
              </span>
            )}
          </h3>

          {stuckAnalyses.length > 0 && (
            <button
              onClick={handleFixStuckAnalyses}
              disabled={fixing}
              className={`flex items-center px-4 py-2 text-sm rounded-lg ${
                fixing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {fixing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Fix All Stuck
                </>
              )}
            </button>
          )}
        </div>

        {/* Fix Results */}
        {fixResults && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Fix Results:</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-green-600">
                ✓ Fixed: {fixResults.fixed?.length || 0}
              </div>
              <div className="text-red-600">
                ✗ Failed: {fixResults.failed?.length || 0}
              </div>
              <div className="text-gray-600">
                ⊘ Skipped: {fixResults.skipped?.length || 0}
              </div>
            </div>
            {fixResults.failed?.length > 0 && (
              <div className="mt-2 text-sm text-red-600">
                {fixResults.failed.map(f => f.error).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Stuck Analyses List */}
        {stuckAnalyses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2 font-medium">ID</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Stuck For</th>
                  <th className="px-4 py-2 font-medium">User</th>
                  <th className="px-4 py-2 font-medium">Dance</th>
                </tr>
              </thead>
              <tbody>
                {stuckAnalyses.map((analysis, index) => (
                  <tr key={analysis.id || index} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">
                      {analysis.id?.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        analysis.type === 'analysis'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {analysis.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                        {analysis.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-orange-600 font-medium">
                      {formatDuration(analysis.stuck_duration_ms)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {analysis.user_id?.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {analysis.dance_type || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p>No stuck analyses found!</p>
            <p className="text-sm">All jobs are processing normally.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueManagement;
